import { Box } from "@minervaproject/fds";
import cuid from "cuid";
import React, { KeyboardEvent, useCallback, useContext, useEffect, useRef, useState } from "react";

import { EmojiInput } from "./emoji_input";
import { EmojiConverter, getClosestCharPrecedingAt } from "./helpers";
import { MentionsInput } from "./mentions_input";

import { FlightAPIContext } from "contexts/flight_api_context";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { addChat } from "shared/reducers/classroom";
import { SidebarPanelType } from "shared/types";
import { ClassEventTypeType } from "shared/types/class_event";
import { classroomSelectors, localSelectors } from "store";
import {
  setChatTextAreaContent,
  setChatTextAreaContentOverride,
  toggleSidebarPanel,
} from "store/reducers/local";
import { createClassEvent } from "store/reducers/monitoring";
import { selectMentionableTags } from "store/selectors/classroom";

function ChatInput() {
  const dispatch = useAppDispatch();
  const flightAPI = useContext(FlightAPIContext);
  const initialChatText = useAppSelector(localSelectors.selectChatTextAreaContent());
  const chatTextOverride = useAppSelector(localSelectors.selectChatTextAreaContentOverride());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const activeRoomId = useAppSelector(classroomSelectors.selectActiveRoomIdForCurrentUser());

  //  Text area search state for emojis, mentions, HC
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [textAreaSelection, setTextAreaSelection] = useState({ start: 0, direction: "none" });
  const messagesInputRef = useRef<HTMLTextAreaElement>(null);
  const textAreaValue = messagesInputRef?.current?.value || "";

  const mentionableTags = useArraySelector(selectMentionableTags());
  const [top3Inputs, setTop3Inputs] = useState<string[]>([]);
  const [showEmojiInput, setShowEmojiInput] = useState(false);
  const [showMentionsInput, setShowMentionsInput] = useState(false);

  useEffect(() => {
    if (textAreaSelection.direction === "none") {
      // Only show the emoji input if the user has typed a colon and the text matches a valid emoji alias
      const precedingColon = getClosestCharPrecedingAt(textAreaValue, ":", textAreaSelection.start);
      if (precedingColon !== null) {
        const searchTerm = textAreaValue.slice(precedingColon, textAreaSelection.start);
        const _showEmojiInput = EmojiConverter.aliases.some(
          (alias: string) => alias.startsWith(searchTerm.slice(1)) && `:${alias}:` !== searchTerm,
        );
        setShowEmojiInput(_showEmojiInput);
        if (_showEmojiInput) {
          const filtered =
            searchTerm.length > 1
              ? EmojiConverter.aliases.filter((alias) => alias.startsWith(searchTerm.slice(1)))
              : EmojiConverter.aliases;
          setTop3Inputs(filtered.slice(0, 3));
          setSelectedIndex(0);
          // No need to check for mentions if we're already displaying EmojiInput. Let's return early.
          return;
        }
      } else {
        setShowEmojiInput(false);
      }
      // Only show the mentions input if the user has typed an @ and the text matches a valid mentionable tag
      const precedingAt = getClosestCharPrecedingAt(textAreaValue, "@", textAreaSelection.start);
      if (precedingAt !== null) {
        const searchTerm = textAreaValue.slice(precedingAt, textAreaSelection.start);
        const _showMentionInput = mentionableTags.some(
          (tag) =>
            tag.toLowerCase().startsWith(searchTerm.toLowerCase()) &&
            tag.toLowerCase() !== searchTerm.toLowerCase(),
        );
        setShowMentionsInput(_showMentionInput);
        if (_showMentionInput) {
          const filtered =
            searchTerm.length > 1
              ? mentionableTags.filter((user: string) =>
                  user.toLowerCase().startsWith(searchTerm.toLowerCase()),
                )
              : mentionableTags;
          setTop3Inputs(filtered.slice(0, 3));
          setSelectedIndex(0);
        }
      } else {
        setShowMentionsInput(false);
      }
    }
  }, [textAreaValue, textAreaSelection.start, textAreaSelection.direction, mentionableTags]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (messagesInputRef.current) {
      timeout = setTimeout(() => messagesInputRef.current?.focus(), 100);
    }
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (
      chatTextOverride &&
      messagesInputRef.current &&
      messagesInputRef.current.value !== chatTextOverride
    ) {
      window.flightLogger.log("[ChatInput] Chat input text changed externally");
      messagesInputRef.current.value = chatTextOverride;
      dispatch(setChatTextAreaContentOverride({ text: "" }));
      dispatch(setChatTextAreaContent({ text: chatTextOverride }));
      messagesInputRef.current.focus();
    }
  }, [chatTextOverride, dispatch]);

  const selectEmojiOrMention = (emoji: boolean, value?: string) => () => {
    const selectedValue = value || top3Inputs[selectedIndex];
    if (!selectedValue) {
      if (messagesInputRef?.current) {
        messagesInputRef.current.value = `${messagesInputRef.current.value} `;
      }
      return;
    }
    const startChar = emoji ? ":" : "@";
    const precedingChar = getClosestCharPrecedingAt(
      textAreaValue,
      startChar,
      textAreaSelection.start,
    );
    if (precedingChar === null) {
      return;
    }
    const replacementValue = emoji ? `:${selectedValue}:` : selectedValue;
    const newString = `${textAreaValue.slice(
      0,
      precedingChar,
    )}${replacementValue} ${textAreaValue.slice(textAreaSelection.start)}`;
    const newCursorPosition = precedingChar + replacementValue.length + 1;
    if (messagesInputRef?.current) {
      messagesInputRef.current.value = `${newString}`;
      messagesInputRef.current.selectionStart = newCursorPosition;
      messagesInputRef.current.selectionEnd = newCursorPosition;
    }
    setChatTextAreaContent({ text: newString });
    setShowMentionsInput(false);
    setSelectedIndex(0);
    setTextAreaSelection({
      start: newCursorPosition,
      direction: "none",
    });
    messagesInputRef.current?.focus?.();
  };

  const selectEmoji = selectEmojiOrMention(true);
  const selectMention = selectEmojiOrMention(false);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (showEmojiInput || showMentionsInput) {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedIndex(selectedIndex === 0 ? top3Inputs.length - 1 : selectedIndex - 1);
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedIndex(selectedIndex === top3Inputs.length - 1 ? 0 : selectedIndex + 1);
        } else if (event.key === "Enter") {
          event.preventDefault();
        } else if (event.key === "Tab") {
          event.preventDefault();
        } else if (event.key === "Escape") {
          event.preventDefault();
          setShowEmojiInput(false);
          setShowMentionsInput(false);
        }
        return;
      }
      if (event.key === "Escape") {
        dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Chat }));
      }
    },
    [dispatch, selectedIndex, showEmojiInput, showMentionsInput, top3Inputs],
  );

  function onKeyUp(event: KeyboardEvent) {
    const target = event.target as HTMLTextAreaElement;
    if (["Enter", "Tab"].includes(event.key)) {
      if (showEmojiInput) {
        selectEmoji();
        return;
      }
      if (showMentionsInput) {
        selectMention();
        return;
      }
    }
    if (event.key === "Enter") {
      const text = target.value.trim();
      if (text) {
        const id = cuid();
        dispatch(
          addChat({
            id,
            createdOn: flightAPI.getServerMilliseconds(),
            userId: currentUserId,
            text,
          }),
        );
        dispatch(
          createClassEvent({
            type: ClassEventTypeType.MESSAGE,
            userId: currentUserId,
            roomId: activeRoomId,
            data: { message_text: text },
          }),
        );
      }
      target.value = "";
    }
    dispatch(setChatTextAreaContent({ text: target.value }));
  }

  function moveCaretToEnd(event: React.FocusEvent) {
    const target = event.target as HTMLTextAreaElement;
    const { value } = target;
    target.value = "";
    target.value = value;
  }

  return (
    <Box className="flex-none chat-input p2">
      {showEmojiInput && (
        <EmojiInput
          top3Inputs={top3Inputs}
          selectedIndex={selectedIndex}
          selectEmoji={selectEmoji}
        />
      )}
      {showMentionsInput && (
        <MentionsInput
          top3Inputs={top3Inputs}
          selectedIndex={selectedIndex}
          selectMention={selectMention}
        />
      )}
      <textarea
        className="border-box body-s text-white bg-black-tint-20 w-full"
        defaultValue={initialChatText}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={moveCaretToEnd}
        placeholder="Type your comment here"
        maxLength={2048}
        ref={messagesInputRef}
        onSelect={(e) => {
          const { selectionEnd, selectionStart, selectionDirection } = e.currentTarget;
          const direction = selectionStart === selectionEnd ? "none" : selectionDirection || "none";
          setTextAreaSelection({
            start: selectionStart || 0,
            direction,
          });
        }}
      />
    </Box>
  );
}

export { ChatInput };
